/**
 * Block 13 PR-G Phase 4A + 4B + 4C — Pure reducer для ContractDraft state.
 *
 * Pattern mirror Block 12 `quoteReducer.ts` (ADR-0019, ADR-0005 — Container
 * useReducer + pure View).
 *
 * **Phase 4A (committed 6f468ad):** hydration + simple field actions для
 * Preamble/Parties/Cost/Warranty sections.
 *
 * **Phase 4B (committed c1da25b):** REQUISITE actions для Customer/Executor
 * anketa rendering — ADD/UPDATE/REMOVE rows + label edit guard.
 *
 * **Phase 4C (this commit):** SPEC actions для Приложения №1 Спецификация —
 * блоки мебели + позиции с 3-source switching:
 *   - `ADD_SPEC_BLOCK` — новый блок мебели в конец `spec[]`.
 *   - `REMOVE_SPEC_BLOCK` — удалить блок целиком (со всеми rows).
 *   - `UPDATE_SPEC_BLOCK_TITLE` — изменить тип мебели блока.
 *   - `ADD_SPEC_ROW` — добавить позицию в блок (с initial source: 'manual').
 *   - `UPDATE_SPEC_ROW` — обновить поле (name/description/cost) одной row.
 *   - `REMOVE_SPEC_ROW` — удалить позицию.
 *   - `SET_SPEC_ROW_SOURCE` — переключатель «Из списка / Вручную / Из КП».
 *   - `BULK_IMPORT_SPEC_ROWS_FROM_QUOTE` — добавить N rows к блоку bulk
 *     (применяется когда user тапает «+ Добавить из КП» на блок — все
 *     compositionRows из выбранного quote добавляются rows с source:'fromQuote').
 *
 * **Action design (discriminated union):** see `ContractAction` type union ниже.
 *
 * Reference: `apps/web/src/features/calculator/quote/quoteReducer.ts` (pattern).
 */

import type {
  ContractCostCustomRow,
  ContractCostSource,
  ContractCurrencyCode,
  ContractDraft,
  ContractLanguage,
  ContractRegion,
  ContractRequisite,
  ContractSpecBlock,
  ContractSpecRow,
  ContractTemplateKey,
  ContractWarrantyUnit,
} from '@pomoshnik/shared';
import {
  CONTRACT_DEFAULT_LANGUAGE_PER_REGION,
  CONTRACT_LANGUAGES_PER_REGION,
  buildContractTemplateKey,
} from '@pomoshnik/shared';

import { genUuid } from '@/lib/uuid';

import {
  applySpecBlockSeed,
  buildDefaultSpecRows,
  buildManualSpecRows,
} from './contractDraftDefaults';
import { computeSpecHash } from './contractSpecHash';

export type ContractHydrationStatus = 'pending' | 'ready';

/**
 * Расширенный state — `ContractDraft` + UI-only hydration tracking. Hydration
 * статус нужен только в Container (skeleton vs form); не персистится в IDB.
 */
export interface ContractFormState extends ContractDraft {
  hydrationStatus: ContractHydrationStatus;
}

/**
 * Discriminated union по `path` для типобезопасных обновлений single-value
 * полей. Каждая ветка определяет валидный `value` тип. Стресс-тест: попытка
 * dispatch `{type: 'SET_FIELD', path: 'totalCost', value: 'abc'}` → TS error.
 */
export type ContractFieldUpdate =
  | { path: 'contractNum'; value: string }
  | { path: 'contractDate'; value: string }
  | { path: 'contractCity'; value: string }
  | { path: 'customerName'; value: string }
  | { path: 'customerRepName'; value: string }
  | { path: 'customerRepBasis'; value: string }
  | { path: 'executorName'; value: string }
  | { path: 'executorRepName'; value: string }
  | { path: 'executorRepBasis'; value: string }
  | { path: 'totalCost'; value: number }
  | { path: 'advance'; value: number }
  // PR-D2 (ADR-0040) — хранимая ставка «Аванс, %» (0–100). null → ручной режим
  // (сумма по `advance`). При заданной ставке UI делает поле `advance` read-only,
  // а сумма выводится производной через `selectAdvance`/`resolveAdvanceAmount`.
  | { path: 'advancePercent'; value: number | null }
  | { path: 'durationDays'; value: number }
  | { path: 'warrantyTerm'; value: number }
  | { path: 'comments'; value: string }
  // Phase 7 hotfix round 4 (ADR-0025 A9, 2026-05-26) — opt-in checkbox для
  // preservation customer values после PDF (см. ContractDraftSchema JSDoc).
  | { path: 'preserveCustomerRequisites'; value: boolean };

/** Phase 4B — какой side anketa обновляется. */
export type RequisiteSide = 'customer' | 'executor';

/** Phase 4B — payload для UPDATE_REQUISITE действия. */
export interface RequisiteUpdate {
  rowId: string;
  /** Field to update. `label` allowed только для removable rows; reducer enforce'ит. */
  field: 'value' | 'label';
  value: string;
}

/**
 * Phase 6.5F Amendment A7 (2026-05-25) — discriminated update payload для
 * UPDATE_SPEC_ROW под new schema.
 *
 * **Before (6.5B A2):** `'label' | 'value' | 'cost'`.
 * **After (6.5F A7):** `'label' | 'type' | 'brand' | 'cost'` — `value` DROPPED,
 * `type` + `brand` separate fields added.
 *
 * Поля:
 *   - `label`: только removable (custom) rows; reducer enforces no-op для
 *     fixed block_cost (label «Стоимость» locked).
 *   - `type`: optional string («ЛДСП», «ПВХ»). Не применимо к block_cost.
 *   - `brand`: optional string («Эггер», «Кира»). Не применимо к block_cost.
 *   - `cost`: number ИЛИ undefined. Применимо к block_cost + custom rows.
 *
 * Reducer не валидирует row.key для type/brand — UI отвечает за rendering
 * (block_cost не показывает type/brand inputs).
 */
export type SpecRowUpdate =
  | { field: 'label'; value: string }
  | { field: 'type'; value: string | undefined }
  | { field: 'brand'; value: string | undefined }
  | { field: 'cost'; value: number | undefined };

/**
 * Phase 6.5B Amendment A3 — discriminated update payload для UPDATE_COST_CUSTOM_ROW.
 * (ContractCostCustomRow остаётся с label/value shape — это отдельная schema
 * для Cost section custom rows, не относится к A7 spec rewrite.)
 */
export type CostCustomRowUpdate =
  | { field: 'label'; value: string }
  | { field: 'value'; value: string };

/**
 * Phase 6.5F Amendment A7 (2026-05-25) — ADD_SPEC_BLOCK optional pre-computed
 * seed для block_cost row.
 *
 * **Before (6.5C):** seeded facade_material / corpus_material / hardware values +
 * block_cost cost.
 * **After (6.5F A7):** только `block_cost` cost (single fixed row). Material
 * slots DROPPED — mapping теперь 1:1 от КП через customRows.
 */
export interface SpecBlockSeedValues {
  block_cost?: number;
}

/**
 * Phase 6.5F Amendment A7 (2026-05-25) — ADD_SPEC_BLOCK optional pre-computed
 * custom rows под new shape.
 *
 * **Before (6.5C):** `{id, label, value, cost?}` — combined value field.
 * **After (6.5F A7):** `{id, label, type?, brand?, cost?}` — type/brand
 * separate (mirror КП CompositionRow {name, itemType, brand}).
 *
 * Caller предоставляет UUIDs (mirrors ADD_CUSTOM_SPEC_ROW pattern).
 */
export interface SpecBlockSeedCustomRow {
  id: string;
  label: string;
  type?: string;
  brand?: string;
  cost?: number;
}

export type ContractAction =
  /** Phase 4A — merge cascade payload (Zustand / IDB / fromQuote seed). Sets status 'ready'. */
  | { type: 'HYDRATE'; payload: Partial<ContractDraft> }
  /** Управление hydrationStatus напрямую (skeleton vs ready). */
  | { type: 'SET_HYDRATION_STATUS'; status: ContractHydrationStatus }
  /** Single-field update — discriminated по `path` для type-safety. */
  | { type: 'SET_FIELD'; update: ContractFieldUpdate }
  /** LanguagePicker: kz.ru ↔ kz.kk, uz.ru ↔ uz.uz. RU имеет 1 lang — no-op. */
  | { type: 'SET_TEMPLATE_KEY'; value: ContractTemplateKey }
  /**
   * Phase 6.5E Item H — RegionPicker (native `<select>`) меняет регион
   * договора. Cascade на templateKey: preserve current language если он
   * доступен в новом регионе; иначе downgrade на default per region. Currency
   * НЕ трогаем (per Aibek decision 2026-05-24: user-explicit override).
   */
  | { type: 'SET_CONTRACT_REGION'; value: ContractRegion }
  /** Cost section chip-segment «Из КП / Вручную». */
  | { type: 'SET_COST_SOURCE'; value: ContractCostSource }
  /** Warranty section chip-segment «месяцев / лет». */
  | { type: 'SET_WARRANTY_UNIT'; value: ContractWarrantyUnit }
  /**
   * Phase 4B — добавить custom row (`removable: true`) к стороне.
   * Caller передаёт `id` (UUID из `genUuid()`) — reducer pure, не дёргает crypto.
   * `label` стартует пустой строкой; пользователь вводит её в UI.
   */
  | { type: 'ADD_REQUISITE_ROW'; side: RequisiteSide; id: string }
  /**
   * Phase 4B — обновить одно поле row. `field: 'value'` разрешён всегда;
   * `field: 'label'` разрешён только для `removable: true` rows (fixed rows
   * имеют immutable labels). Reducer guard игнорирует label edits на fixed rows.
   */
  | { type: 'UPDATE_REQUISITE'; side: RequisiteSide; update: RequisiteUpdate }
  /**
   * Phase 4B — удалить custom row (`removable: true`). Fixed rows
   * (default per-region rows) защищены — попытка remove игнорируется (no-op).
   */
  | { type: 'REMOVE_REQUISITE_ROW'; side: RequisiteSide; rowId: string }
  /**
   * Phase 6.5B Amendment A2 — добавить новый блок мебели. Caller передаёт id
   * (UUID) и title (e.g. «Кухня»). Reducer seeds 4 fixed default rows
   * (`buildDefaultSpecRows()`) автоматически — material rows + block_cost.
   *
   * **Phase 6.5C extension** — optional `seedValues` + `customRows` для atomic
   * «Добавить из КП» flow. Когда оба не переданы — поведение идентично 6.5B
   * (back-compatible). Когда переданы — reducer применяет seed values на
   * соответствующие default rows + appends custom rows.
   *
   * **Phase 6.5G A8 extension** — optional `manualSeed: true` для «Заполнить
   * вручную» flow (entry с Главной → ChoiceSheetCreateMode либо «+ Добавить
   * мебель» внутри Договора). Когда `true` — reducer использует
   * `buildManualSpecRows()` (17 default позиций + block_cost) вместо
   * `buildDefaultSpecRows()`. Mutually exclusive с `seedValues`/`customRows`
   * (manualSeed wins если caller передал оба — UI этого не делает, но
   * reducer guard явный).
   */
  | {
      type: 'ADD_SPEC_BLOCK';
      id: string;
      title: string;
      seedValues?: SpecBlockSeedValues;
      customRows?: SpecBlockSeedCustomRow[];
      manualSeed?: boolean;
    }
  /** Удалить блок целиком (со всеми rows внутри). */
  | { type: 'REMOVE_SPEC_BLOCK'; blockId: string }
  /** Изменить title (тип мебели) блока. */
  | { type: 'UPDATE_SPEC_BLOCK_TITLE'; blockId: string; title: string }
  /**
   * Phase 6.5B Amendment A2 — добавить custom row в блок (key='custom-<uuid>',
   * removable: true). Caller передаёт rowId (UUID). Label/value стартуют
   * пустыми; cost undefined (user может задать через UPDATE_SPEC_ROW).
   */
  | { type: 'ADD_CUSTOM_SPEC_ROW'; blockId: string; rowId: string }
  /**
   * Phase 6.5B Amendment A2 — обновить одно поле row (label | value | cost).
   * `field='label'` enforced только для removable rows (fixed rows immutable).
   */
  | { type: 'UPDATE_SPEC_ROW'; blockId: string; rowId: string; update: SpecRowUpdate }
  /**
   * Phase 6.5B Amendment A2 — удалить custom row (`removable: true`). Fixed
   * rows защищены — попытка remove игнорируется (no-op).
   */
  | { type: 'REMOVE_SPEC_ROW'; blockId: string; rowId: string }
  /**
   * Phase 6.5B Amendment A1 — chip-segment "тенге / рубль / сум" в
   * SectionCostAndTerms (UI ships в 6.5C). Decoupled от template language.
   */
  | { type: 'SET_CURRENCY_CODE'; value: ContractCurrencyCode }
  /**
   * Phase 6.5B Amendment A3 — добавить custom row в Cost section. Caller
   * передаёт rowId (UUID). Label/value стартуют пустыми; user заполняет.
   */
  | { type: 'ADD_COST_CUSTOM_ROW'; rowId: string }
  /** Phase 6.5B Amendment A3 — обновить label или value cost custom row. */
  | { type: 'UPDATE_COST_CUSTOM_ROW'; rowId: string; update: CostCustomRowUpdate }
  /** Phase 6.5B Amendment A3 — удалить cost custom row. */
  | { type: 'REMOVE_COST_CUSTOM_ROW'; rowId: string }
  /**
   * PR-G Phase 7 (P-009 transient PII flow) — очистить `customerRequisites[].value`
   * после успешного PDF Download/Share. Сохраняет label/key/removable (структура
   * анкеты Заказчика остаётся, transient values сбрасываются). `executorRequisites`
   * НЕ trogается — это собственные реквизиты пользователя приложения, не PII клиента.
   *
   * Triggers (post-success only — preserve user input on error/cancel):
   *   - BottomActions.handleDownloadPdf после generateAndDownloadContractPdf resolved
   *   - BottomActions.handleShareResult для `shared` | `downloaded` | `desktop-direct`
   * Не triggers:
   *   - error / cancelled / preview / autosave / edit hydration
   */
  | { type: 'CLEAR_CUSTOMER_REQUISITES_VALUES' }
  /**
   * PR-C5 (ADR-0039, amends ADR-0036) — подтверждаемый пересев РОДНОГО блока из КП.
   *
   * Диспатчится `ContractFormContainer` при подтверждении пользователем модалки
   * «Обновить данные из КП?» («Да») ИЛИ клике ссылки «↻ Обновить из КП».
   * `payload` — результат `mapQuoteStateToContractDraft(quote)` (новый сид:
   * `payload.spec[0]` = КП-блок), дополненный bookkeeping (`seededFromQuoteAt` /
   * `lastQuoteUpdatedAt`). Всё ручное (реквизиты, comments, сроки, валюта) — preserve.
   *
   * Поведение (ADR-0039): **полная замена родного блока**. Находим блок
   * `block.sourceQuoteId === state.fromQuoteId` (fallback legacy: первый блок с
   * `block_cost`). Его `title` + `rows` (включая `block_cost` с КП-ценой)
   * заменяются на `payload.spec[0]`; `block.id` и `sourceQuoteId` сохраняются.
   * **Остальные блоки не трогаются** (добавленные через «+ Добавить мебель» целы).
   * MUST NOT `nextSpec = payload.spec` (затрёт добавленные блоки). Родной блок
   * не найден → spec не меняется (graceful), только bookkeeping.
   *
   * Pristine/dirty-ветвление (ADR-0036 #3/#4) и `state.totalCost` (vestigial) —
   * удалены. Стоимость = `sumSpecCost` (сумма всех блоков). `lastQuoteUpdatedAt`
   * И `quotePromptAckAt` бампятся (это «реальный сев» — ссылка скрывается).
   */
  | { type: 'PARTIAL_RESEED_CONTRACT'; payload: Partial<ContractDraft> }
  /**
   * PR-C5 (ADR-0039, Вариант 2) — пользователь ответил «Нет» на промпт «Обновить
   * данные из КП?» (или закрыл модалку Esc/overlay). Бампит ТОЛЬКО
   * `quotePromptAckAt = ackAt` (= `quote.updatedAt`) → модалка не появляется снова
   * для этой версии КП, но `lastQuoteUpdatedAt` не трогается → ссылка «↻ Обновить
   * из КП» остаётся видна между визитами (пока КП новее последнего реального сева).
   * Spec и прочее состояние не меняются.
   */
  | { type: 'ACK_QUOTE_RESEED_PROMPT'; ackAt: number };

export function contractReducer(
  state: ContractFormState,
  action: ContractAction,
): ContractFormState {
  switch (action.type) {
    case 'HYDRATE': {
      const merged: ContractFormState = {
        ...state,
        ...action.payload,
        hydrationStatus: 'ready',
      };
      // Phase 6.5D Item #5 — defensive guard: ensure ≥1 spec block exists
      // после hydration. Old drafts (созданные до 6.5D auto-seed change)
      // могут иметь `spec: []` в IDB; seed empty default block чтобы user
      // увидел структуру 4 default rows сразу при открытии (mirror new draft
      // initial state). New drafts уже имеют seed через
      // `createInitialContractDraft`; fromQuote seeding передаёт filled spec.
      //
      // UUID generation внутри reducer: matches existing pattern в case
      // 'ADD_SPEC_BLOCK' (line ~278) которое тоже использует
      // `buildDefaultSpecRows()` (генерирует 4 UUIDs внутри) + caller-supplied
      // block.id. «Reducer pure» note в line-152 относится к ADD_REQUISITE_ROW
      // (caller передаёт id для consistency с simpler row actions); для
      // spec-block actions seeded-factory pattern уже established с 6.5B.
      if (merged.spec.length === 0) {
        merged.spec = [{ id: genUuid(), title: '', rows: buildDefaultSpecRows() }];
      }
      return merged;
    }
    case 'SET_HYDRATION_STATUS':
      return { ...state, hydrationStatus: action.status };
    case 'SET_FIELD': {
      const { path, value } = action.update;
      return { ...state, [path]: value } as ContractFormState;
    }
    case 'SET_TEMPLATE_KEY':
      return { ...state, templateKey: action.value };
    case 'SET_CONTRACT_REGION': {
      // Phase 6.5E Item H — region change cascade:
      //   - current language доступен в новом регионе → preserve
      //   - иначе → downgrade на CONTRACT_DEFAULT_LANGUAGE_PER_REGION[newRegion]
      // currencyCode НЕ меняем (per Aibek decision: user-explicit override).
      const currentLang = (state.templateKey.split('.')[1] ?? 'ru') as ContractLanguage;
      const availableLangs = CONTRACT_LANGUAGES_PER_REGION[action.value];
      const nextLang = availableLangs.includes(currentLang)
        ? currentLang
        : CONTRACT_DEFAULT_LANGUAGE_PER_REGION[action.value];
      const nextTemplateKey = buildContractTemplateKey(action.value, nextLang);
      if (nextTemplateKey === state.templateKey) return state;
      return { ...state, templateKey: nextTemplateKey };
    }
    case 'SET_COST_SOURCE':
      return { ...state, costSource: action.value };
    case 'SET_WARRANTY_UNIT':
      return { ...state, warrantyUnit: action.value };
    case 'ADD_REQUISITE_ROW': {
      const newRow: ContractRequisite = {
        id: action.id,
        key: `custom-${action.id}`,
        label: '',
        value: '',
        removable: true,
      };
      return action.side === 'customer'
        ? { ...state, customerRequisites: [...state.customerRequisites, newRow] }
        : { ...state, executorRequisites: [...state.executorRequisites, newRow] };
    }
    case 'UPDATE_REQUISITE': {
      const { rowId, field, value } = action.update;
      const list = action.side === 'customer' ? state.customerRequisites : state.executorRequisites;
      // Reference semantics consistency с REMOVE_REQUISITE_ROW: nonexistent rowId
      // или label edit на fixed row (silent no-op) — возвращаем state by reference.
      const target = list.find((r) => r.id === rowId);
      if (!target) return state;
      if (field === 'label' && !target.removable) return state;
      const next = list.map((row) => (row.id !== rowId ? row : { ...row, [field]: value }));
      return action.side === 'customer'
        ? { ...state, customerRequisites: next }
        : { ...state, executorRequisites: next };
    }
    case 'REMOVE_REQUISITE_ROW': {
      const list = action.side === 'customer' ? state.customerRequisites : state.executorRequisites;
      // Защитный guard: попытка remove fixed row (removable=false) — no-op.
      const target = list.find((r) => r.id === action.rowId);
      if (!target || !target.removable) return state;
      const next = list.filter((r) => r.id !== action.rowId);
      return action.side === 'customer'
        ? { ...state, customerRequisites: next }
        : { ...state, executorRequisites: next };
    }
    case 'ADD_SPEC_BLOCK': {
      // Phase 6.5G A8 — three seed modes:
      //   1. `manualSeed: true` → buildManualSpecRows (17 + block_cost) для
      //      «Заполнить вручную» (Главная либо «+ Добавить мебель» внутри
      //      Договора). Mutually exclusive с seedValues/customRows.
      //   2. seedValues / customRows → applySpecBlockSeed для «Взять данные
      //      из КП» (atomic 1:1 КП copy). Existing 6.5C flow.
      //   3. ни флагов ни payload → single block_cost (6.5B back-compat для
      //      HYDRATE auto-seed и edge cases).
      const rows = action.manualSeed
        ? buildManualSpecRows()
        : applySpecBlockSeed(buildDefaultSpecRows(), action.seedValues, action.customRows);
      const newBlock: ContractSpecBlock = {
        id: action.id,
        title: action.title,
        rows,
      };
      return { ...state, spec: [...state.spec, newBlock] };
    }
    case 'REMOVE_SPEC_BLOCK': {
      if (!state.spec.some((b) => b.id === action.blockId)) return state;
      return { ...state, spec: state.spec.filter((b) => b.id !== action.blockId) };
    }
    case 'UPDATE_SPEC_BLOCK_TITLE': {
      if (!state.spec.some((b) => b.id === action.blockId)) return state;
      return {
        ...state,
        spec: state.spec.map((b) => (b.id !== action.blockId ? b : { ...b, title: action.title })),
      };
    }
    case 'ADD_CUSTOM_SPEC_ROW': {
      if (!state.spec.some((b) => b.id === action.blockId)) return state;
      // Phase 6.5F Amendment A7 (2026-05-25) — custom row под new shape:
      // key='custom-<uuid>', removable=true, label/type/brand/cost пустые
      // (user заполнит через UI: label «Столешница», type «ЛДСП», brand «Эггер»,
      // cost optional). `value` field DROPPED — type/brand separate.
      const newRow: ContractSpecRow = {
        id: action.rowId,
        key: `custom-${action.rowId}`,
        label: '',
        type: undefined,
        brand: undefined,
        cost: undefined,
        removable: true,
      };
      return {
        ...state,
        spec: state.spec.map((b) =>
          b.id !== action.blockId ? b : { ...b, rows: [...b.rows, newRow] },
        ),
      };
    }
    case 'UPDATE_SPEC_ROW': {
      const block = state.spec.find((b) => b.id === action.blockId);
      if (!block) return state;
      const target = block.rows.find((r) => r.id === action.rowId);
      if (!target) return state;
      const { field, value } = action.update;
      // Phase 6.5B Amendment A2 — label edits only allowed для removable rows
      // (fixed rows have immutable labels set by buildDefaultSpecRows).
      if (field === 'label' && !target.removable) return state;
      return {
        ...state,
        spec: state.spec.map((b) =>
          b.id !== action.blockId
            ? b
            : {
                ...b,
                rows: b.rows.map((r) => (r.id !== action.rowId ? r : { ...r, [field]: value })),
              },
        ),
      };
    }
    case 'REMOVE_SPEC_ROW': {
      const block = state.spec.find((b) => b.id === action.blockId);
      if (!block) return state;
      const target = block.rows.find((r) => r.id === action.rowId);
      // Phase 6.5B Amendment A2 — only removable (custom) rows можно удалять.
      // Fixed rows (4 defaults) защищены от REMOVE — попытка = no-op.
      if (!target || !target.removable) return state;
      return {
        ...state,
        spec: state.spec.map((b) =>
          b.id !== action.blockId ? b : { ...b, rows: b.rows.filter((r) => r.id !== action.rowId) },
        ),
      };
    }
    case 'SET_CURRENCY_CODE': {
      // Phase 6.5B Amendment A1 — user-controlled currency, decoupled от
      // template language. Default seeded в createInitialContractDraft через
      // CONTRACT_CURRENCY_PER_REGION[region].
      return { ...state, currencyCode: action.value };
    }
    case 'ADD_COST_CUSTOM_ROW': {
      // Phase 6.5B Amendment A3 — pure factory: caller передаёт rowId, reducer
      // создаёт row с пустыми label/value. UI ships в 6.5C.
      const newRow: ContractCostCustomRow = {
        id: action.rowId,
        label: '',
        value: '',
      };
      return { ...state, costCustomRows: [...state.costCustomRows, newRow] };
    }
    case 'UPDATE_COST_CUSTOM_ROW': {
      if (!state.costCustomRows.some((r) => r.id === action.rowId)) return state;
      const { field, value } = action.update;
      return {
        ...state,
        costCustomRows: state.costCustomRows.map((r) =>
          r.id !== action.rowId ? r : { ...r, [field]: value },
        ),
      };
    }
    case 'REMOVE_COST_CUSTOM_ROW': {
      if (!state.costCustomRows.some((r) => r.id === action.rowId)) return state;
      return {
        ...state,
        costCustomRows: state.costCustomRows.filter((r) => r.id !== action.rowId),
      };
    }
    case 'CLEAR_CUSTOMER_REQUISITES_VALUES': {
      // P-009 transient PII clear: customer values reset, executor untouched.
      // Reference semantics: если все values уже пустые — возвращаем state by
      // reference (no-op) для idempotency + чтобы autosave не trigger'ил IDB
      // write на повторный CLEAR (e.g. double-click Download).
      const allEmpty = state.customerRequisites.every((r) => r.value === '');
      if (allEmpty) return state;
      return {
        ...state,
        customerRequisites: state.customerRequisites.map((r) => ({ ...r, value: '' })),
      };
    }
    case 'PARTIAL_RESEED_CONTRACT': {
      // PR-C5 (ADR-0039) — полная замена РОДНОГО блока из КП. См. JSDoc выше.
      const { payload } = action;
      const newBlock = payload.spec?.[0];

      let nextSpec = state.spec;
      if (newBlock !== undefined) {
        const nativeIdx = findNativeBlockIndex(state.spec, state.fromQuoteId);
        if (nativeIdx !== -1) {
          const old = state.spec[nativeIdx]!;
          nextSpec = state.spec.map((block, i) =>
            i !== nativeIdx
              ? block // прочие блоки целы (добавленные через «+ Добавить мебель»).
              : {
                  // Полная замена: title + rows из КП; id + sourceQuoteId сохраняем.
                  id: old.id,
                  title: newBlock.title,
                  rows: newBlock.rows,
                  sourceQuoteId: old.sourceQuoteId ?? state.fromQuoteId ?? undefined,
                },
          );
        }
        // Родной блок не найден (удалён) → spec не трогаем (graceful), bookkeeping ниже.
      }

      const quoteUpdatedAt = payload.lastQuoteUpdatedAt ?? state.lastQuoteUpdatedAt;
      return {
        ...state,
        spec: nextSpec,
        // seededSpecHash держим консистентным (pristine-ветвление удалено).
        seededSpecHash: computeSpecHash(nextSpec),
        seededFromQuoteAt: payload.seededFromQuoteAt ?? state.seededFromQuoteAt,
        // Реальный сев → бампим оба таймстампа (ссылка «↻» скрывается).
        lastQuoteUpdatedAt: quoteUpdatedAt,
        quotePromptAckAt: quoteUpdatedAt,
      };
    }
    case 'ACK_QUOTE_RESEED_PROMPT': {
      // PR-C5 (ADR-0039, Вариант 2) — «Нет»: бампим только quotePromptAckAt.
      // lastQuoteUpdatedAt не трогаем → ссылка «↻ Обновить из КП» остаётся видна.
      return { ...state, quotePromptAckAt: action.ackAt };
    }
    default: {
      assertNever(action);
      return state;
    }
  }
}

/**
 * PR-C5 (ADR-0039) — индекс «родного» блока (созданного из родного КП).
 * Точная адресация по маркеру `sourceQuoteId === fromQuoteId`; fallback для
 * legacy-договоров без маркера — первый блок, содержащий `block_cost`-строку
 * (КП-импорт всегда создаёт один такой блок первым). Возвращает -1, если не найден.
 */
function findNativeBlockIndex(
  spec: readonly ContractSpecBlock[],
  fromQuoteId: string | null,
): number {
  if (fromQuoteId !== null) {
    const marked = spec.findIndex((b) => b.sourceQuoteId === fromQuoteId);
    if (marked !== -1) return marked;
  }
  return spec.findIndex((b) => b.rows.some((r) => r.key === 'block_cost'));
}

function assertNever(value: never): never {
  throw new Error(`contractReducer: unhandled action ${JSON.stringify(value)}`);
}
